import ImageDownload from "../../components/ImageDownload"

const data = {
    title: '5.2. Werkwijze alternatiefontwikkeling',
    content: <>
        <h3>Doelstelling</h3>
        <p>De ruimtelijke impact van de energiedoelstellingen is bepaald aan de hand van informatie uit de RES-rapportages. Elke RES-regio heeft haar bod/opwekdoelstelling op eigen wijze onderbouwd. Deze informatie is zoveel mogelijk op één niveau gebracht, passend bij het karakter en abstractieniveau van dit MER. Waar informatie niet eenduidig beschikbaar was, zijn aannames gedaan en heeft omrekening plaatsgevonden aan de hand van grove kengetallen. Dit MER is daardoor minder genuanceerd dan de afzonderlijke beelden van de regio’s.</p>
        <p>Het totaal bod in TWh binnen de regio’s is het uitgangspunt en ook hoe dit binnen de regio’s is uitgesplitst naar windenergie, zonne-energie en het aandeel bestaand (+ in ontwikkeling) en nieuw daarin. De nieuw te ontwikkelen opwekdoelstelling voor windenergie en zonne-energie (na aftrek zon op dak) is via de vuistregels uit de handreiking RES (NP RES) vertaald in aantallen windturbines en hectares zonne-energie per regio. Hierbij wordt uitgegaan van:</p>
        
        <ul>
            <li>Windturbines met een vermogen van 5,6 MW 
                <ul>
                    <li>Volgens de handreiking zijn 45 tot 70 windturbines nodig voor de opwekking van 1 TWh per jaar. In dit MER wordt uitgegaan van het gemiddelde, afgerond dus 58 turbines voor 1 TWh. De opgetelde doelstelling van de energieregio’s leidt tot 32 nieuwe turbines.</li>
                </ul>
            </li>
            <li>Een zonneveld waarvan, afhankelijk van de oriëntatie (oost-west of zuid) 850 a 1100 respectievelijk 1400 a 1500 hectare nodig is voor de opwekking van 1 TWh per jaar. In dit MER is uitgegaan van de meest royale omvang. De opgetelde doelstelling van de energieregio’s leidt op deze manier tot een opgave van 1500 hectare nieuw zonneveld in Zuid-Holland. </li>
        </ul>
 
        <p>Onderstaande tabel geeft het overzicht van de doelstellingen per energieregio, vertaald naar aantallen windturbines en hectares zonneveld. Waar door de regio’s een marge is aangegeven in het opwekdoel, is voor dit MER uitgegaan van het maximum. </p>


<table className="paragraaf-5-2" id="paragraaf-5-2">
    <thead>
        <tr>
            <th>Energieregio </th>
            <th>Totaal bod TWh/jr </th>
            <th>Nieuw te realiseren TWh/jr </th>
            <th>Nieuw grootschalig zon en wind TWh/jr </th>
            <th>Windturbines (aantal) </th>
            <th>Zonneveld (hectares) </th>
        </tr>
    </thead>
    <tbody>
        <tr>
    <td>Alblasserwaard </td>
    <td>0,32 </td>
    <td>0,24 </td>
    <td>0,121 </td>
    <td>7 </td>
    <td>0 </td>
    </tr>
    <tr>
<td>Drechtsteden </td>
<td>0,37 </td>
<td>0,248 </td>
<td>0,13 </td>
<td>1 </td>
<td>180 </td>
</tr>
    <tr>
<td>Goeree Overflakkee </td>
<td>0,85 </td>
<td>0,052 </td>
<td>0 </td>
<td>0 </td>
<td>0 </td>
</tr>
    <tr>
<td>Hoeksche Waard </td>
<td>0,39-0,48 </td>
<td>0,003-0,093 </td>
<td>0 </td>
<td>0 </td>
<td>0 </td>
</tr>
    <tr>
<td>Holland Rijnland </td>
<td>1,14 </td>
<td>0,94 </td>
<td>0,499 </td>
<td>5 </td>
<td>480 </td>
</tr>
    <tr>
<td>Midden-Holland </td>
<td>0,44 </td>
<td>0,338 </td>
<td>0,103 </td>
<td>0 </td>
<td>155 </td>
</tr>
    <tr>
<td>Rotterdam – Den Haag </td>
<td>2,8-3,2 </td>
<td>1,444 </td>
<td>0,752 </td>
<td>19 </td>
<td>645 </td>
</tr>
    <tr className="bold">
<td>Provincie Zuid-Holland totaal </td>
<td>6,4-6,8 </td>
<td>2,848-3,278 </td>
<td>1,6 </td>
<td>32 (ca 0,6 TWh/jr) </td>
<td>1460 (ca 1 TWh/jr) </td>
</tr>    </tbody>
</table>


        <p>Bij de beelden en de beschrijving van de alternatieven worden aantallen windturbines genoemd. Hierbij is bedoeld: het aantal windturbines met een vermogen van 5,6 MW dat nodig is om het beoogde opwekdoel te realiseren, afgerond op hele turbines. Dit aantal is indicatief en wordt gehanteerd ten behoeve van een eenduidig beeld op provinciaal niveau. Afhankelijk van het in de praktijk toe te passen turbinetype kan het aantal turbines dat nodig is afwijken. Voor zonne-energie geldt hetzelfde principe. Afhankelijk van de opstelling, oriëntatie, type panelen en de terreininrichting kan de benodigde oppervlakte om de energiedoelstelling te bereiken verschillen. In de alternatieven zijn de oppervlaktes afgerond op 5 hectare.</p>

        <h3>Beschikbare ruimte </h3>
        <p>Als tussenstap op weg naar de alternatiefontwikkeling is de potentiële ruimte binnen de zoekgebieden bepaald. Voor windenergie is daarbij in eerste instantie uitgegaan van de beoordeling van deze zoekgebieden op geluid, externe veiligheid, de beschermde landschappen (beschermingscategorieën) en er is ook gekeken naar buisleidingen, hoogspanning, wegen en spoorwegen. Hiermee is de feitelijke zoekruimte ingeperkt ten behoeve van de alternatiefontwikkeling. Overigens wordt opgemerkt dat ook binnen de gebieden met belemmeringen mogelijk ontwikkelruimte ligt. Hiervoor is echter gedetailleerd onderzoek op locatie nodig dat het detailniveau van dit MER te boven gaat. In tweede instantie is door middel van een globale luchtfotocontrole gekeken of de ruimtes die vrij zijn van harde belemmeringen ook functioneel geschikt worden geacht voor plaatsing van windturbines. Hiervoor geldt ook weer dat bij nadere bestudering van de lokale situatie en uitwerking van de plannen kan blijken dat er meer of minder ruimte beschikbaar is dan in het kader van dit MER wordt ingeschat. Voor zonne-energie is een vergelijkbare analyse met luchtfotocontrole uitgevoerd met als resultaat een inschatting van de beschikbare ruimte.</p>

        <h3>Clustering van zoekgebieden</h3>
        <p>De zoekgebieden zijn zeer verschillend qua maat en wijze van begrenzing. Sommige zoekgebieden zijn exact op kavelniveau begrensd, terwijl andere zoekgebieden slechts globaal een zone aangeven, bijvoorbeeld de omgeving van een snelweg. Om beter aan te sluiten bij het provinciale schaalniveau en karakter van dit MER is ervoor gekozen de zoekgebieden ten behoeve van de alternatieven samen te voegen tot clusters. Voor windenergie en voor zonne-energie zijn aparte clusters aangehouden, die elkaar deels overlappen. De grenzen tussen de regio’s zijn gerespecteerd: er zijn geen zoekgebieden uit verschillende regio’s samengevoegd binnen één cluster. In de alternatieven worden de aantallen windturbines en hectares zonne-energie gekoppeld aan de clusters, niet aan de afzonderlijke zoekgebieden. </p>

        <h3>Omgang met begrenzing van de RES-regio’s</h3>
        <p>Realisatie van het eigen bod van de RES’en binnen de grenzen van de regio is het vertrekpunt voor alternatief 1 en 2. Voor zover dat bij windenergie niet mogelijk lijkt binnen de aangegeven zoekgebieden, vindt verschuiving over de grenzen van de regio’s plaats. Dit is uiteraard een theoretische herverdeling, alleen bedoeld om op provinciaal niveau de impact in beeld te krijgen en staat los van afspraken binnen en tussen de RES’en.</p>
        <p>Op provinciaal niveau is er binnen de zoekgebieden, na aftrek van de gebieden met belemmeringen, ruimte om meer windturbines te realiseren dan nodig voor het opgetelde bod van de RES’en. Bij de alternatieven is hierbinnen, vanuit de specifieke optiek van het betreffende alternatief, de best passende selectie gemaakt.</p>
        <p>Voor zonne-energie is bij deze alternatieven geen herverdeling tussen de regio’s nodig. In de alternatieven 3 en 4 vindt vanuit andere argumenten (landschap en energie-infrastructuur) herverdeling van windenergie over de energieregio’s plaats. Voor de herverdeling van zonne-energie bij deze alternatieven zijn er binnen de energieregio’s voldoende mogelijkheden en worden daarom de regiogrenzen aangehouden. </p>

        <ImageDownload title="Clusters met zoekgebieden voor windenergie" defaultImage="energietransitie/images/20220505_Overzicht clusters wind.jpg" largeImage="energietransitie/images/20220505_Overzicht clusters wind.pdf" fileSize="1 MB" />
        <ImageDownload title="Clusters met zoekgebieden voor zonne-energie" defaultImage="energietransitie/images/20220505_Overzicht clusters zon.jpg" largeImage="energietransitie/images/20220505_Overzicht clusters zon.jpg" fileSize="1,2 MB" />

        <h3>Kaartbeelden van de alternatieven</h3>
        <p>De alternatieven zijn weergegeven op kaart en zijn zowel te bekijken in een online viewer als in de vorm van een samengesteld ‘vast’ beeld. De viewer heeft als voordeel de mogelijkheid tot eenvoudig in- en zoomen, schakelen tussen de alternatieven en weergeven van onderliggende kaartlagen.</p>
        <p>De ‘vaste beelden’ geven een compleet overzicht met grafisch weergegeven aantallen windturbines en oppervlaktes zonneveld (een rechthoekig blok op schaal van de kaart). De intensiteit van de kleur van de clusters staat voor de hoeveelheid energieopwekking. Voor elk alternatief is het verschil ten opzichte van alternatief RES 1.0- basis weergegeven. Waar sprake is van uitbreiding ten opzichte van het basisalternatief is dit weergegeven in rode symbolen. Waar sprake is van vermindering ten opzichte van het basisalternatief is dit weergegeven in licht grijs. De bestaande windturbines en de locaties die in ontwikkeling zijn, staan ook op kaart aangegeven. </p>

    </>,
    prev: '/energietransitie-05-05-2022/5/1',
    next: '/energietransitie-05-05-2022/5/3',

}

export default data